import { useEffect, useState } from 'react';
import Icon from './Icon';
import Button from './Button';

/** Pulsing placeholder rows shown while table data loads. */
export function TableSkeleton({ rows = 5, message }) {
  return (
    <div className="w-full p-6 space-y-3" role="status" aria-live="polite">
      <div className="animate-pulse space-y-3">
        <div className="h-9 bg-surface-container rounded-lg w-full" />
        {Array.from({ length: rows }, (_, i) => (
          <div key={i} className="h-12 bg-surface-container-low rounded-lg w-full" />
        ))}
      </div>
      {message && (
        <p className="text-center text-xs text-on-surface-variant pt-2 flex items-center justify-center gap-2">
          <Icon name="progress_activity" size={18} className="animate-spin text-secondary" />
          {message}
        </p>
      )}
    </div>
  );
}

/** Centered icon + title + text, with an optional action below. */
const ICON_TONES = {
  ok: 'bg-secondary-container/30 text-secondary',
  alert: 'bg-error-container/50 text-error',
  neutral: 'bg-surface-container text-on-surface-variant',
};

export function EmptyState({ icon = 'task_alt', title, description, action, tone = 'ok', role }) {
  const iconTone = ICON_TONES[tone] ?? ICON_TONES.ok;
  return (
    <div role={role} className="w-full py-16 px-6 flex flex-col items-center justify-center text-center">
      <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-3 ${iconTone}`}>
        <Icon name={icon} size={28} />
      </div>
      <h3 className="text-base font-bold text-on-surface mb-1">{title}</h3>
      {description && <p className="text-xs text-on-surface-variant max-w-sm mb-4">{description}</p>}
      {action}
    </div>
  );
}

/**
 * Error panel with an optional Retry button. `retryAfter` (seconds) keeps Retry
 * disabled with a countdown first, e.g. after a 429.
 */
export function ErrorState({ icon = 'cloud_off', title, description, onRetry, retryAfter = 0 }) {
  const [wait, setWait] = useState(retryAfter);

  useEffect(() => {
    setWait(retryAfter);
    if (!retryAfter) return undefined;
    const timer = setInterval(() => setWait((s) => (s <= 1 ? (clearInterval(timer), 0) : s - 1)), 1000);
    return () => clearInterval(timer);
  }, [retryAfter]);

  return (
    <EmptyState
      role="alert"
      tone="alert"
      icon={icon}
      title={title}
      description={description}
      action={
        onRetry && (
          <Button icon="refresh" onClick={onRetry} disabled={wait > 0}>
            {wait > 0 ? `Retry in ${wait}s` : 'Retry'}
          </Button>
        )
      }
    />
  );
}
